import CanvasPluginProvider from '@/components/canvas/CanvasPluginProvider'
import { useMeasurementStore } from '@/store/measurements'
import Konva from 'konva'
import { useCamerasStore } from '@/store/cameras'
import { CanvasPluginSettings } from '@/types/Canvas'
import { Reactive } from 'vue'

type Position = {
  time: string
  height: number
  score: number
  width: number
  xCenter: number
  yCenter: number
  camera: string
  label: string
}

type ConfigType = {
  enabled: boolean
}

const LABEL_COLORS: Record<string, string> = {
  Broiler: 'blue',
}

export class PositionPlugin extends CanvasPluginProvider<ConfigType> {
  reactiveProperties: Reactive<{ enabled: boolean; loading: boolean; positions: Position[] }>

  constructor(settings: CanvasPluginSettings, config: ConfigType) {
    super(settings, 'position', config, true, true)
    this.reactiveProperties = { loading: false, enabled: settings.enabled, positions: [] }
  }

  fetchInformation(): [Promise<void>, AbortController] {
    this.reactiveProperties.positions = []
    this.draw()
    const controller = new AbortController()
    const alignedStart = this.settings.timestamp.startOf('second').toMillis()
    const promise = new Promise<void>((resolve, reject) => {
      useMeasurementStore()
        .loadGenericMeasurement(
          'position',
          {
            groupByTags: true,
            time: { start: alignedStart, end: alignedStart + 1 },
          },
          controller.signal
        )
        .then(result => {
          this.reactiveProperties.positions = result
          resolve()
        })
        .catch(reject)
    })
    return [promise, controller]
  }

  draw() {
    this.group.destroyChildren()
    if (!this.reactiveProperties.positions?.length) return

    for (const position of this.reactiveProperties.positions) {
      const cameraId = position.camera
      const elementPosition = this.settings.elementPositions[cameraId]
      if (!elementPosition) continue

      const camera = useCamerasStore().getCameraById(undefined, cameraId)
      if (!camera) continue

      const x = ((position.xCenter - position.width / 2) / camera.realWorldWidth) * elementPosition.width
      const y = ((position.yCenter - position.height / 2) / camera.realWorldHeight) * elementPosition.height
      const w = (position.width / camera.realWorldWidth) * elementPosition.width
      const h = (position.height / camera.realWorldHeight) * elementPosition.height

      const transform = new Konva.Transform()
      transform.translate(elementPosition.x, elementPosition.y)
      transform.rotate(elementPosition.rotation * (Math.PI / 180))

      const color = LABEL_COLORS[position.label] ?? 'red'

      const box = new Konva.Rect({
        x: transform.point({ x, y }).x,
        y: transform.point({ x, y }).y,
        width: w,
        height: h,
        strokeWidth: 2,
        stroke: color,
        strokeScaleEnabled: false,
        rotation: elementPosition.rotation,
      })

      box.on('mouseenter', ev => {
        this.settings.tooltipEnable({
          x: ev.evt.clientX + 2,
          y: ev.evt.clientY + 2,
          text: `${position.label} (${(position.score * 100).toFixed(2)}%)`,
        })
      })

      box.on('mousemove', ev => {
        this.settings.tooltipEnable({
          x: ev.evt.clientX + 2,
          y: ev.evt.clientY + 2,
          text: `${position.label} (${(position.score * 100).toFixed(2)}%)`,
        })
      })

      box.on('mouseleave', () => {
        this.settings.tooltipDisable()
      })

      box.preventDefault(false)
      this.group.add(box)
    }
  }

  doesConfigChangeNeedRerender(): boolean {
    return false
  }

  doesConfigChangeNeedReload(): boolean {
    return false
  }
}
